import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, CalendarCheck } from "lucide-react";

export default function FinalCTA({
  title = "Ready to start a service request?",
  subtitle = "Tell us what's happening — we'll show the contractor exactly what's going on before the callback.",
  primaryCta = { label: "Request HVAC Service", to: "/request-service", icon: Wrench },
  secondaryCta = {
    label: "Schedule Maintenance",
    to: "/request-service?intent=maintenance",
    icon: CalendarCheck,
  },
  fineprint = "Demo intake flow — no real emergency dispatch.",
  testId = "final-cta",
}) {
  const PrimaryIcon = primaryCta?.icon;
  const SecondaryIcon = secondaryCta?.icon;
  return (
    <section
      data-testid={testId}
      aria-labelledby="final-cta-heading"
      className="relative overflow-hidden bg-white py-20 sm:py-24"
    >
      <div className="container-page">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="relative overflow-hidden rounded-3xl border border-aqua/20 bg-brand-gradient p-8 text-white shadow-lift sm:p-12"
        >
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-aqua/30 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-sun/20 blur-3xl" aria-hidden="true" />

          <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2
                id="final-cta-heading"
                className="font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl"
              >
                {title}
              </h2>
              {subtitle && (
                <p className="mt-3 max-w-xl text-base text-white/80 sm:text-lg">
                  {subtitle}
                </p>
              )}
              {fineprint && (
                <p className="mt-3 text-xs text-white/60">{fineprint}</p>
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
              {primaryCta && (
                <Link
                  to={primaryCta.to}
                  data-testid={`${testId}-primary`}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3.5 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
                >
                  {PrimaryIcon && <PrimaryIcon className="h-4 w-4" />}
                  {primaryCta.label}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
              {secondaryCta && (
                <Link
                  to={secondaryCta.to}
                  data-testid={`${testId}-secondary`}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/95 px-6 py-3.5 text-sm font-semibold text-navy shadow-soft transition-colors hover:bg-white"
                >
                  {SecondaryIcon && <SecondaryIcon className="h-4 w-4 text-aqua-deep" />}
                  {secondaryCta.label}
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
